import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import {
  ProcedureParameterConfirmationMethod,
  ProcedureParameterUsageValueType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/TableConstants';
import {ParameterUsage} from 'procedures/ProcedureDomainTypes';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import {ellipsize} from 'util/NameUtil';

export interface ParameterUsageJsonValue {
  parameterUsages: Array<ParameterUsage>;
  targetParameterUsageType?: ProcedureParameterUsageValueType;
  confirmationMethod?: ProcedureParameterConfirmationMethod;
}

interface ParameterUsageCellProps {
  typedValue: TypedValue;
  navigateToStep: (stepCid: string) => void;
  propertyLabel?: string;
}

/**
 * Render list of parameter usages as step names linking to each step page.
 *
 * @param parameterUsages Parameter usages to show
 * @param navigateToStep Handler to navigate to particular step
 * @param shouldEllipsize Whether to ellipsize each step name
 * @param truncateAtStepNumber If defined, how many steps to render before truncating.
 */
function ParameterUsagesList({
  parameterUsages,
  navigateToStep,
  shouldEllipsize,
  truncateAtStepNumber,
}: {
  parameterUsages: Array<ParameterUsage>;
  navigateToStep: (stepCid: string) => void;
  shouldEllipsize: boolean;
  truncateAtStepNumber?: number;
}): JSX.Element {
  return (
    <div className={css(styles.wrapText)}>
      {parameterUsages.slice(0, truncateAtStepNumber).map((paramUsage) => (
        <a
          onClick={() => {
            navigateToStep(paramUsage.stepCid);
          }}
          title={paramUsage.stepName}
          key={`${paramUsage.stepCid}-${paramUsage.valueType}`}
        >
          {`${shouldEllipsize ? ellipsize(paramUsage.stepName, 35) : paramUsage.stepName}\n`}
        </a>
      ))}
    </div>
  );
}

/**
 * Modal to render all parameter usages, opened from expand button in cell.
 *
 * Does not ellipsize step names, or truncate how many steps are rendered.
 */
function ParameterUsageExpandedModal({
  parameterUsages,
  navigateToStep,
  onRequestClose,
  propertyLabel,
}: {
  parameterUsages: Array<ParameterUsage>;
  navigateToStep: (stepCid: string) => void;
  onRequestClose: () => void;
  propertyLabel?: string;
}): JSX.Element {
  return (
    <Modal
      buttons={[
        {
          disabled: false,
          style: ButtonStyle.PRIMARY,
          submit: true,
          text: 'Done',
        },
      ]}
      title={propertyLabel ?? 'Confirmed in'}
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestClose}
    >
      <ParameterUsagesList
        parameterUsages={parameterUsages}
        navigateToStep={(stepCid) => {
          navigateToStep(stepCid);
          onRequestClose();
        }}
        shouldEllipsize={false}
      />
    </Modal>
  );
}

/**
 * Filter parameter usages to de-duplicate & remove incorrect usage types.
 *
 * @param targetParameterUsageType If specified, only usages of this type will be included.
 * @param parameterUsages Usages to filter
 *
 * @return Filtered list of parameter usages
 */
function filterParameterUsages(
  parameterUsages: Array<ParameterUsage>,
  targetParameterUsageType?: ProcedureParameterUsageValueType
): Array<ParameterUsage> {
  const seenUsages: {[parameterTypeKey: string]: boolean} = {};
  return parameterUsages.filter((usage) => {
    const {parameterVersionId, valueType, stepCid} = usage;

    // Skip usages of the incorrect parameter usage type
    if (targetParameterUsageType != null && valueType !== targetParameterUsageType) {
      return false;
    }

    // De-duplicate steps for a specific parameter + usage type by step cid since they'll link to the same page
    const parameterTypeKey = `${parameterVersionId}-${valueType}-${stepCid}`;
    if (seenUsages[parameterTypeKey]) {
      return false;
    }
    seenUsages[parameterTypeKey] = true;
    return true;
  });
}

/**
 * Cell renderer for parameter usages.
 *
 * Renders step names linking to the individual step confirmation pages. If the step list has to be truncated,
 * supports expanding out into a modal view to view all steps.
 *
 * @param typedValue Json typed value with parameter usages info
 * @param navigateToStep Handler to navigate to a step confirmation page
 * @param propertyLabel Label to use in modal; set to the column header & defaults to "Confirmed in"
 */
export default function ParameterUsageCell({
  typedValue,
  navigateToStep,
  propertyLabel,
}: ParameterUsageCellProps): JSX.Element | null {
  const expandUsagesHandler = useCallback(
    (parameterUsages: Array<ParameterUsage>) => {
      runModal(ParameterUsageExpandedModal, {
        parameterUsages,
        navigateToStep,
        propertyLabel,
      });
    },
    [propertyLabel, navigateToStep]
  );

  if (typedValue.type === BaseTypedValueType.JSON) {
    const {parameterUsages, targetParameterUsageType, confirmationMethod} =
      typedValue.jsonValue as ParameterUsageJsonValue;
    const filteredParameterUsages: Array<ParameterUsage> = filterParameterUsages(
      parameterUsages ?? [],
      targetParameterUsageType
    );

    if (filteredParameterUsages?.length > 0) {
      const truncateAtStepNumber = 4;
      return (
        <>
          <ParameterUsagesList
            parameterUsages={filteredParameterUsages}
            navigateToStep={navigateToStep}
            shouldEllipsize={true}
            truncateAtStepNumber={truncateAtStepNumber}
          />
          {filteredParameterUsages.length > truncateAtStepNumber && (
            <div>
              <a
                onClick={() => {
                  expandUsagesHandler(filteredParameterUsages);
                }}
              >{`+${filteredParameterUsages.length - truncateAtStepNumber} more`}</a>
              <BenchlingIcon
                icon="expand-right-full"
                onClick={() => {
                  expandUsagesHandler(filteredParameterUsages);
                }}
                className={css(styles.expandIcon)}
              />
            </div>
          )}
        </>
      );
    } else {
      // Plate parameters cannot have measurements recorded so are shown as N/A instead
      const defaultText =
        confirmationMethod === ProcedureParameterConfirmationMethod.PLATE_MAP &&
        targetParameterUsageType === ProcedureParameterUsageValueType.MEASUREMENT
          ? 'N/A'
          : 'Add table in steps to confirm';
      return <div className={css(styles.placeholder)}>{defaultText}</div>;
    }
  }
  return null;
}

const styles = StyleSheet.create({
  placeholder: {
    fontStyle: 'italic',
  },
  wrapText: {
    whiteSpace: 'pre-wrap',
  },
  expandIcon: {
    cursor: 'pointer',
    color: BLUE_LIGHT,
    float: 'right',
  },
});
